/**
 * Boot-time relayer validation. Imports chain configs, so it must never be
 * imported by a chain config — see the note in `./resolve`.
 */
import { ChainIdEnum } from "../../enums/chain";
import { CHAIN_CONFIGS } from "../chains/configs";
import { getChainIdFromEnv } from "../chains/getters";
import { RelayerConfigError } from "./errors";
import { getRelayerUpstream } from "./resolve";

/**
 * @throws {RelayerConfigError} when the configured chain is gasless but has no
 * relayer this build could reach.
 *
 * A relayer recovers the order signer against ONE deployment, so a build
 * pointed at another chain's relayer has every order rejected as an invalid
 * signature even though the signature is correct. That is indistinguishable
 * from a real signing bug at runtime, so it is caught at boot instead.
 */
export const assertRelayerConfigValid = (): void => {
  const chainId = getChainIdFromEnv();
  const config = CHAIN_CONFIGS.get(chainId);
  if (!config?.gasless) {
    return;
  }

  if (!config.relayerUrl) {
    throw new RelayerConfigError(
      `Chain ${ChainIdEnum[chainId]} (${chainId}) has gasless: true but no ` +
        "relayerUrl. Set REACT_APP_RELAYER_URL, or add the chain to " +
        "src/config/relayers/upstreams.json."
    );
  }

  // A same-origin path is only reachable if something proxies it: setupProxy.js
  // in dev, nginx in production. Only the DEV proxy needs the upstream — in
  // production nginx owns the route, so throwing there would blank the app for
  // a reason that does not apply to it.
  if (
    process.env.NODE_ENV !== "production" &&
    config.relayerUrl.startsWith("/") &&
    !getRelayerUpstream(chainId)
  ) {
    throw new RelayerConfigError(
      `Chain ${ChainIdEnum[chainId]} (${chainId}) routes its relayer through ` +
        `"${config.relayerUrl}", but src/config/relayers/upstreams.json has no ` +
        `entry for ${chainId}, so the dev proxy has nowhere to forward to. ` +
        "Add the chain's relayer origin there."
    );
  }
};
